import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid2,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Container,
  Typography,
  Paper,
  SelectChangeEvent,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Coach, coachInit } from "../../model/entity/Coach";
import { Gender } from "../../model/common/Gender";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  CoachValidationObj,
  coachValidationObjInit,
  validateCoach,
} from "../../model/validation/CoachValidation";
import handleAxiosError from "../../components/ErrorHandler";
import { notifErr, notifOk } from "../../components/Functions";
import { postFetcher } from "../../components/Fetchers";

const NewCoach = () => {
  const navigate = useNavigate();
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [coach, setCoach] = useState<Coach>(coachInit);
  const [coachErr, setCoachErr] = useState<CoachValidationObj>(
    coachValidationObjInit
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCoach({ ...coach, [e.target.name]: e.target.value });
  };

  const handleGenderInputChange = (e: SelectChangeEvent) => {
    setCoach({ ...coach, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (triedSubmit) setCoachErr(validateCoach(coach));
  }, [coach, triedSubmit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTriedSubmit(true);

    const coachErrors = validateCoach(coach);
    setCoachErr(coachErrors);

    if (Object.values(coachErrors).some((val) => val === false)) return;

    try {
      await postFetcher(`${process.env.REACT_APP_API_URL}/coaches`, coach);
      notifOk("Trenér úspěšně přidán.");
      navigate("/overview/coaches");
    } catch (err) {
      notifErr(handleAxiosError(err));
    }
  };

  const handleCancel = () => {
    navigate("/overview/coaches");
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pb: "2rem",
      }}
    >
      <Paper elevation={3} sx={{ padding: "1.5rem" }}>
        <Typography variant="h5" sx={{ marginBottom: "1.5rem" }}>
          Nový trenér
        </Typography>
        <Grid2 container spacing={3} size={{ xs: 12, md: 6 }}>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Jméno"
              required
              name="firstName"
              value={coach.firstName}
              error={!coachErr.firstName}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Příjmení"
              required
              name="lastName"
              value={coach.lastName}
              error={!coachErr.lastName}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Přezdívka"
              required
              name="nickname"
              value={coach.nickname}
              error={!coachErr.nickname}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD.MM.YYYY"
                label="Datum narození"
                sx={{ width: "100%" }}
                name="birthDate"
                value={coach.birthDate === "" ? null : dayjs(coach.birthDate)}
                onChange={(date) =>
                  setCoach({
                    ...coach,
                    birthDate: date ? date.format("YYYY-MM-DD") : "",
                  })
                }
                slotProps={{
                  textField: {
                    size: "small",
                    required: true,
                    error:
                      !coachErr.birthDate &&
                      !dayjs(coach.birthDate, "YYYY-MM-DD", true).isValid(),
                  },
                  field: { clearable: true },
                }}
              />
            </LocalizationProvider>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControl required size="small" fullWidth>
              <InputLabel>Pohlaví</InputLabel>
              <Select
                label="Pohlaví"
                name="gender"
                value={coach.gender}
                error={!coachErr.gender}
                onChange={handleGenderInputChange}
              >
                <MenuItem value={Gender.MALE}>Muž</MenuItem>
                <MenuItem value={Gender.FEMALE}>Žena</MenuItem>
              </Select>
            </FormControl>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Email"
              type="email"
              required
              name="email"
              value={coach.email}
              error={!coachErr.email}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Telefon"
              type="tel"
              required
              name="phone"
              value={coach.phone}
              error={!coachErr.phone}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Adresa bydliště"
              required
              name="address"
              value={coach.address}
              error={!coachErr.address}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Rodné číslo (bez lomítka)"
              name="nationalId"
              value={coach.nationalId}
              error={!coachErr.nationalId}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Hodinová sazba (Kč)"
              type="number"
              required
              name="wage"
              value={coach.wage}
              error={!coachErr.wage}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Sazba daně (%)"
              type="number"
              required
              name="tax"
              value={coach.tax}
              error={!coachErr.tax}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Bankovní účet"
              required
              name="accountNumber"
              value={coach.accountNumber}
              error={!coachErr.accountNumber}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Poznámka"
              multiline
              rows={4}
              name="note"
              value={coach.note}
              error={!coachErr.note}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2
            container
            size={{ xs: 12 }}
            sx={{ justifyContent: "right", gap: 2 }}
          >
            <Grid2>
              <Button
                variant="outlined"
                className="cancelBtn"
                onClick={handleCancel}
              >
                Zrušit
              </Button>
            </Grid2>
            <Grid2>
              <Button
                variant="outlined"
                className="submitBtn"
                onClick={handleSubmit}
              >
                Přidat
              </Button>
            </Grid2>
          </Grid2>
        </Grid2>
      </Paper>
    </Container>
  );
};

export default NewCoach;
